import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { publicQrGroupApi } from '@/features/qr-group/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';

/**
 * Shared-QR landing (`/sg/:groupId`). Several stations sit behind one QR, so
 * the very first thing the consumer does is pick which station they're at.
 * Picking forwards into the normal single-station public survey
 * (`/public-surveys?surveyId=&stationId=`) — from there the wheel prizes and
 * responses are scoped to the chosen station, no special-casing downstream.
 */
export function SharedQrLanding() {
  const { groupId } = useParams<{ groupId: string }>();
  const gId = Number(groupId);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const t = useT();

  const preview = params.get('preview') === '1' ? '&preview=1' : '';
  const v = params.get('v');
  const vQuery = v ? `&v=${encodeURIComponent(v)}` : '';

  const groupQ = useQuery({
    queryKey: queryKeys.qrGroups.publicDetail(gId),
    queryFn: () => publicQrGroupApi.getPublic(gId),
    enabled: Number.isFinite(gId),
    retry: false,
  });

  const goToStation = (stationId: number, surveysId: number | null) =>
    navigate(
      `/public-surveys?surveyId=${surveysId}&stationId=${stationId}${vQuery}${preview}`,
      { replace: true },
    );

  if (!Number.isFinite(gId)) {
    return <CenteredCard heading={t('sharedQr.invalid.title')} body={t('sharedQr.invalid.body')} />;
  }
  if (groupQ.isLoading) {
    return <CenteredCard heading={t('sharedQr.loading.title')} body={t('sharedQr.loading.body')} />;
  }
  if (groupQ.isError || !groupQ.data) {
    return <CenteredCard heading={t('sharedQr.invalid.title')} body={t('sharedQr.invalid.body')} />;
  }

  const { name, surveysId, stations } = groupQ.data;

  // Degenerate cases: a single station behaves like a normal QR.
  if (stations.length === 1) {
    return <Navigate to={`/public-surveys?surveyId=${surveysId}&stationId=${stations[0].id}${vQuery}${preview}`} replace />;
  }
  if (stations.length === 0) {
    return <CenteredCard heading={t('sharedQr.empty.title')} body={t('sharedQr.empty.body')} />;
  }

  return (
    <div className="grid min-h-screen place-items-center bg-[var(--color-xk-bg-deep)] px-4 text-[var(--color-xk-lavender)]">
      <div className="w-full max-w-sm rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-6">
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          {name}
        </p>
        <h1 className="mt-1 text-xl font-black" style={{ fontFamily: 'var(--font-display)' }}>
          {t('sharedQr.pick.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-xk-text-muted)]">{t('sharedQr.pick.body')}</p>

        <div className="mt-4 space-y-2">
          {stations.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goToStation(s.id, surveysId)}
              className="flex w-full items-center gap-3 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-4 py-3 text-left transition-colors hover:border-[var(--color-accent-ink)]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-xk-surface)]">
                <MapPin className="h-4 w-4 text-[var(--color-accent-ink)]" />
              </span>
              <span className="font-semibold">{s.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CenteredCard({ heading, body }: { heading: string; body: string }) {
  return (
    <div className="grid min-h-screen place-items-center bg-[var(--color-xk-bg-deep)] px-4 text-[var(--color-xk-lavender)]">
      <div className="max-w-sm rounded-2xl border border-[var(--color-xk-border)] p-6 text-center">
        <h1 className="text-lg font-bold">{heading}</h1>
        <p className="mt-2 text-sm opacity-70">{body}</p>
      </div>
    </div>
  );
}
